import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ChatText, EnvelopeSimple, BellRinging, FloppyDisk, PaperPlaneTilt, ShieldCheck, Phone } from "@phosphor-icons/react";

const SEVERITIES = ["critical", "high", "medium", "low"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/settings/notifications").then((r) => setS(r.data)); }, []);

  const up = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const toggleSev = (sev) => setS((p) => ({ ...p, notify_severities: p.notify_severities.includes(sev) ? p.notify_severities.filter((x) => x !== sev) : [...p.notify_severities, sev] }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/notifications", s);
      setS(data);
      toast.success("Alert delivery settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/notifications/test");
      Object.entries(data.results).forEach(([ch, r]) => r === "sent" ? toast.success(`${ch.toUpperCase()} test sent`) : toast.error(`${ch.toUpperCase()}: ${r}`));
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const enableBrowser = async () => {
    if (!("Notification" in window)) { toast.error("Browser notifications not supported"); return; }
    const p = await Notification.requestPermission();
    if (p === "granted") { toast.success("Browser alerts enabled"); new Notification("SentryVision", { body: "Browser alerts are on." }); }
    else toast.error("Permission denied");
  };

  if (!s) return <div className="text-sm text-[#71717A] font-mono-data">Loading settings…</div>;

  const Field = ({ label, k, type = "text", placeholder, ph }) => (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1.5">{label}</label>
      <input data-testid={`field-${k}`} type={type} value={s[k] ?? ""} onChange={(e) => up(k, type === "number" ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder || (ph ? "•••••• (leave blank to keep)" : "")}
        className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm font-mono-data outline-none focus:border-[#007AFF] transition-colors" />
    </div>
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-8 space-y-4">
        {/* AI Phone Call (Bland AI) */}
        <div className="bg-[#121214] border border-[#BF5AF2]/40 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2"><Phone size={16} weight="bold" className="text-[#BF5AF2]" /> AI Phone Call (Bland AI)</h2>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="call-enabled" checked={s.call_enabled} onChange={(e) => up("call_enabled", e.target.checked)} className="accent-[#BF5AF2] w-4 h-4" /> Enabled</label>
          </div>
          <p className="text-xs text-[#71717A] mb-4">On a high-confidence theft/vandalism alert, Bland AI places an automated voice call to your manager and reads out what happened. Get your API key at app.bland.ai → Settings/Keys. It stays on your server.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Bland API Key" k="bland_api_key" type="password" ph />
            <Field label="Call This Number" k="call_to" placeholder="+1 manager number (E.164)" />
            <Field label="Voice (optional)" k="call_voice" placeholder="maya" />
          </div>
        </div>

        {/* SMS */}
        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2"><ChatText size={16} weight="bold" className="text-[#007AFF]" /> SMS Alerts (Twilio) <span className="text-[9px] text-[#71717A]">optional</span></h2>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="sms-enabled" checked={s.sms_enabled} onChange={(e) => up("sms_enabled", e.target.checked)} className="accent-[#007AFF] w-4 h-4" /> Enabled</label>
          </div>
          <p className="text-xs text-[#71717A] mb-4">Get keys at console.twilio.com — Account SID, Auth Token & a Twilio phone number. Your keys stay on your server; they're never shared.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Account SID" k="twilio_sid" placeholder="ACxxxxxxxx" />
            <Field label="Auth Token" k="twilio_token" type="password" ph />
            <Field label="From Number" k="twilio_from" placeholder="+1..." />
            <Field label="Send To" k="sms_to" placeholder="+1 manager number" />
          </div>
        </div>

        {/* Email */}
        <div className="bg-[#121214] border border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2"><EnvelopeSimple size={16} weight="bold" className="text-[#32D74B]" /> Email Alerts <span className="text-[9px] text-[#32D74B]">no setup needed</span></h2>
            <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" data-testid="email-enabled" checked={s.email_enabled} onChange={(e) => up("email_enabled", e.target.checked)} className="accent-[#007AFF] w-4 h-4" /> Enabled</label>
          </div>
          <p className="text-xs text-[#71717A] mb-4">Just enter a recipient — email sends through our built-in service, no SMTP or keys required. (Advanced: fill the SMTP fields to send from your own mail server instead.)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Send To" k="email_to" placeholder="manager@store.com" />
            <div />
            <Field label="SMTP Host (optional)" k="smtp_host" placeholder="leave blank = built-in" />
            <Field label="SMTP Port" k="smtp_port" type="number" />
            <Field label="SMTP User" k="smtp_user" placeholder="you@store.com" />
            <Field label="SMTP Password" k="smtp_pass" type="password" ph />
          </div>
        </div>
      </div>

      <div className="xl:col-span-4 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA] mb-3">Notify On Severity</h3>
          <div className="flex flex-wrap gap-2">
            {SEVERITIES.map((sev) => (
              <button key={sev} data-testid={`sev-${sev}`} onClick={() => toggleSev(sev)}
                className={`px-2.5 py-1 text-xs font-mono-data border uppercase transition-colors ${s.notify_severities.includes(sev) ? "bg-[#007AFF]/15 text-[#3395FF] border-[#007AFF]/50" : "bg-[#1A1A1E] text-[#71717A] border-white/10 hover:text-white"}`}>{sev}</button>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <button data-testid="save-settings" onClick={save} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors disabled:opacity-60"><FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : "Save Settings"}</button>
            <button data-testid="test-settings" onClick={test} disabled={testing} className="w-full flex items-center justify-center gap-2 bg-[#1A1A1E] border border-white/10 hover:border-[#007AFF] text-white py-2.5 text-sm transition-colors disabled:opacity-60"><PaperPlaneTilt size={16} /> {testing ? "Sending…" : "Send Test Alert"}</button>
          </div>
        </div>

        <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-white mb-1 flex items-center gap-2"><BellRinging size={14} weight="fill" /> Instant Browser Alerts</h3>
          <p className="text-xs text-[#E4E4E7] leading-relaxed mb-3">No keys needed. Get a desktop pop-up the moment a high/critical alert fires while the dashboard is open.</p>
          <button data-testid="enable-browser" onClick={enableBrowser} className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors">Enable Browser Alerts</button>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4">
          <p className="text-[11px] text-[#71717A] leading-relaxed flex items-start gap-2"><ShieldCheck size={14} className="text-[#32D74B] shrink-0 mt-0.5" /> Your Twilio/SMTP credentials are stored on your own server and used only to send your alerts. Secrets are masked after saving — leave a secret field blank to keep the existing value.</p>
        </div>
      </div>
    </div>
  );
}
